'use client';

import { useRouter } from 'next/navigation';
import type { ReactNode } from 'react';

import { useSubscriptionQuery } from '../api/billingApi';

import type { Tier } from '../types/billing.types';

interface Props {
  /** Minimum tier required for the wrapped content to be interactive. */
  requires: Exclude<Tier, 'free'>;
  /** Concise plain-English description of what's being gated.
   *  e.g. "AI Coach", "more than 1 connected account". */
  feature: string;
  /** Optional extra explanation shown beneath the headline. */
  detail?: string;
  /** What to render UNDER the overlay (kept in place + visible but blurred /
   *  non-interactive so the user can see what they're getting). */
  children: ReactNode;
}

const TIER_RANK: Record<Tier, number> = {
  free: 0,
  starter: 1,
  pro: 2,
  elite: 3,
};

/**
 * Wrap any Pro/Elite-only feature with this. When the user's current
 * tier meets the requirement, renders `children` untouched. When it
 * doesn't, renders the children blurred + non-interactive with an
 * "Upgrade to <required tier>" overlay on top — never just an empty
 * div or a broken-looking form.
 *
 * Source of truth is `/billing/subscription` (kept fresh by RTK Query),
 * not a cached client value — so a Stripe-driven tier upgrade refreshes
 * the overlay state automatically when the webhook lands.
 */
export function PaywallOverlay({ requires, feature, detail, children }: Props) {
  const router = useRouter();
  const { data: sub } = useSubscriptionQuery();

  const currentTier: Tier = sub?.tier ?? 'free';
  const allowed = TIER_RANK[currentTier] >= TIER_RANK[requires];

  if (allowed) return <>{children}</>;

  const trialExpired =
    sub?.status === 'trial_expired' ||
    (sub?.status === 'canceled' && currentTier === 'free');

  return (
    <div style={{ position: 'relative' }}>
      <div
        aria-hidden
        style={{
          filter: 'blur(3px) saturate(0.7)',
          opacity: 0.55,
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      >
        {children}
      </div>
      <div
        role="region"
        aria-label={`${feature} requires the ${requires} plan`}
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
        }}
      >
        <div
          style={{
            maxWidth: 420,
            width: '100%',
            background: 'var(--pm-paper)',
            border: '1px solid var(--pm-rule)',
            borderRadius: 14,
            padding: '22px 24px',
            boxShadow: '0 20px 45px rgba(22,20,15,0.10)',
            textAlign: 'center',
          }}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '3px 10px',
              borderRadius: 999,
              background: trialExpired ? 'var(--pm-coral-soft)' : 'var(--pm-cream-2)',
              color: trialExpired ? 'var(--pm-coral-deep)' : 'var(--pm-ink-2)',
              fontSize: 10.5,
              fontWeight: 600,
              letterSpacing: 1.2,
              textTransform: 'uppercase',
              marginBottom: 10,
            }}
          >
            {trialExpired ? 'Trial ended' : `${requires.toUpperCase()} required`}
          </span>
          <div
            style={{
              fontFamily: 'var(--pm-serif)',
              fontSize: 19,
              letterSpacing: -0.3,
              fontWeight: 500,
              color: 'var(--pm-ink)',
              lineHeight: 1.3,
              marginBottom: 8,
            }}
          >
            {trialExpired
              ? 'Pick a plan to keep using this'
              : `Unlock ${feature}`}
          </div>
          <div
            style={{
              fontSize: 13,
              color: 'var(--pm-ink-3)',
              lineHeight: 1.5,
              marginBottom: 16,
            }}
          >
            {detail ??
              (trialExpired
                ? 'Your free trial has ended. Your data is still here — subscribe to re-enable enforcement and monitoring.'
                : `Available on PropMind ${requires.charAt(0).toUpperCase() + requires.slice(1)} and above.`)}
          </div>
          <button
            type="button"
            onClick={() => router.push('/billing')}
            style={{
              padding: '10px 22px',
              borderRadius: 10,
              background: 'var(--pm-ink-fixed)',
              color: '#fff',
              border: '1px solid var(--pm-ink-fixed)',
              fontSize: 13.5,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            See plans
          </button>
        </div>
      </div>
    </div>
  );
}
